import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { renderTextWithLinks } from '../utils/renderTextWithLinks';
import { sanitizeNotificationHtml } from '../utils/notificationHtml';
import { notificationApiService, NotificationMessageFormat } from '../services/notificationApiService';

export { htmlToPlainText } from '../utils/notificationHtml';

const VIDEO_SPLIT_REGEX = /(<div data-cf-video="[0-9a-fA-F]{32}"><\/div>)/g;
const VIDEO_UID_REGEX = /data-cf-video="([0-9a-fA-F]{32})"/;
const REFRESH_LEAD_MS = 60_000;
const MIN_REFRESH_MS = 5_000;

type LightboxMedia = { kind: 'image'; src: string; alt: string } | { kind: 'video'; src: string };

/** The signed player URL with autoplay on, for the full-screen view. */
const withAutoplay = (src: string): string => {
    try {
        const url = new URL(src);
        url.searchParams.set('autoplay', 'true');
        return url.toString();
    } catch {
        return src;
    }
};

/** A signed player URL's poster image (the same token, so no extra API call). */
const posterFor = (iframeSrc: string): string | null =>
    /\/iframe(\?.*)?$/.test(iframeSrc)
        ? iframeSrc.replace(/\/iframe(\?.*)?$/, '/thumbnails/thumbnail.jpg?time=1s&height=540&fit=crop')
        : null;

/**
 * An image or video filling nearly the whole screen inside a bordered frame. Its own overlay rather
 * than a second Modal: it sits above the notification modal that is already open, and Escape (taken
 * in the capture phase) closes only this, not the notification underneath it.
 */
const MediaLightbox: React.FC<{ media: LightboxMedia; onClose: () => void }> = ({ media, onClose }) => {
    const theme = useTheme();
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        closeRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [onClose]);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={media.kind === 'image' ? 'Image' : 'Video'}
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 10300,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                padding: '2vh 2vw',
                boxSizing: 'border-box',
                display: 'flex',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    position: 'relative',
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    border: `4px solid ${theme.colors.surface}`,
                    outline: `2px solid ${theme.colors.primary}`,
                    borderRadius: theme.borderRadius.lg,
                    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6)',
                    overflow: 'hidden',
                }}
            >
                {media.kind === 'image' ? (
                    <img
                        src={media.src}
                        alt={media.alt}
                        style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block', cursor: 'default' }}
                    />
                ) : (
                    <iframe
                        title="Video"
                        src={withAutoplay(media.src)}
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                        allowFullScreen
                        style={{ width: '100%', height: '100%', border: 0 }}
                    />
                )}
                <button
                    ref={closeRef}
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 40,
                        height: 40,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        lineHeight: 1,
                        fontSize: 26,
                        borderRadius: '50%',
                        border: `2px solid ${theme.colors.surface}`,
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                        color: '#fff',
                        cursor: 'pointer',
                    }}
                >
                    ×
                </button>
            </div>
        </div>
    );
};

export interface RichContentProps {
    message: string;
    messageFormat?: NotificationMessageFormat | string | null;
    notificationId?: string;
    navigate: (url: string) => void;
}

const VideoEmbed: React.FC<{ notificationId?: string; videoUid: string; onOpen: (iframeSrc: string) => void }> = ({ notificationId, videoUid, onOpen }) => {
    const theme = useTheme();
    const hostRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
    const [src, setSrc] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
            if (timer.current) clearTimeout(timer.current);
        };
    }, []);

    useEffect(() => {
        if (visible || !hostRef.current || typeof IntersectionObserver === 'undefined') return;
        const obs = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                setVisible(true);
                obs.disconnect();
            }
        });
        obs.observe(hostRef.current);
        return () => obs.disconnect();
    }, [visible]);

    const load = useCallback(async () => {
        if (!notificationId) {
            setError('Video unavailable');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await notificationApiService.getVideoPlayback(notificationId, videoUid);
            if (!alive.current) return;
            if (!res.success || !res.iframeSrc) {
                setError(res.message || 'Video unavailable');
                return;
            }
            setSrc(res.iframeSrc);
            const exp = res.expiresAt ? new Date(res.expiresAt).getTime() : NaN;
            if (Number.isFinite(exp)) {
                const wait = Math.max(MIN_REFRESH_MS, exp - Date.now() - REFRESH_LEAD_MS);
                if (timer.current) clearTimeout(timer.current);
                timer.current = setTimeout(() => void load(), wait);
            }
        } catch {
            if (alive.current) setError('Video unavailable');
        } finally {
            if (alive.current) setLoading(false);
        }
    }, [notificationId, videoUid]);

    useEffect(() => {
        if (visible) void load();
    }, [visible, load]);

    return (
        <div ref={hostRef} style={{ margin: `${theme.spacing.sm} 0`, maxWidth: '100%' }}>
            {src && !error && (
                // A poster the reader clicks to watch full screen: the player itself would swallow
                // the click, so the inline view never mounts an iframe.
                <button
                    type="button"
                    aria-label="Play video full screen"
                    onClick={() => onOpen(src)}
                    style={{
                        position: 'relative',
                        display: 'block',
                        width: '100%',
                        maxWidth: 560,
                        aspectRatio: '16 / 9',
                        padding: 0,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        backgroundColor: '#000',
                        backgroundImage: posterFor(src) ? `url("${posterFor(src)}")` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        border: `2px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.md,
                    }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                            border: '3px solid #fff',
                            color: '#fff',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 26,
                            paddingLeft: 4,
                        }}
                    >
                        <i className="fas fa-play" />
                    </span>
                </button>
            )}
            {!src && loading && !error && (
                <div style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading video...
                </div>
            )}
            {error && (
                <div role="alert" style={{ color: theme.colors.status.error, fontSize: theme.typography.fontSize.sm }}>
                    <i className="fas fa-exclamation-circle" aria-hidden="true" /> {error}{' '}
                    <button
                        type="button"
                        onClick={() => void load()}
                        style={{
                            border: `1px solid ${theme.colors.border.default}`,
                            borderRadius: theme.borderRadius.sm,
                            background: theme.colors.surface,
                            color: theme.colors.text.primary,
                            cursor: 'pointer',
                            fontSize: theme.typography.fontSize.sm,
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}
        </div>
    );
};

export const RichContent: React.FC<RichContentProps> = ({ message, messageFormat, notificationId, navigate }) => {
    const theme = useTheme();
    const isHtml = messageFormat === 'html';
    const [lightbox, setLightbox] = useState<LightboxMedia | null>(null);
    const closeLightbox = useCallback(() => setLightbox(null), []);
    const segments = useMemo(
        () => (isHtml ? sanitizeNotificationHtml(message ?? '').split(VIDEO_SPLIT_REGEX) : []),
        [isHtml, message],
    );

    if (!isHtml) {
        return <>{renderTextWithLinks(message ?? '', navigate, theme)}</>;
    }

    // Images arrive as sanitized HTML, so the click is picked up on the container. An image inside a
    // link opens full screen instead of following the link.
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const img = (e.target as HTMLElement | null)?.closest?.('img') as HTMLImageElement | null;
        if (!img?.src) return;
        e.preventDefault();
        setLightbox({ kind: 'image', src: img.src, alt: img.alt || 'Image' });
    };

    return (
        <div className="rich-content" style={{ wordBreak: 'break-word' }} onClick={handleClick}>
            <style>{`
                .rich-content img { max-width: 100%; height: auto; cursor: zoom-in; }
                .rich-content a { color: ${theme.colors.primary}; text-decoration: underline; }
                .rich-content table { border-collapse: collapse; margin: ${theme.spacing.sm} 0; }
                .rich-content th, .rich-content td { border: 1px solid ${theme.colors.border.default}; padding: 4px 8px; vertical-align: top; }
                .rich-content th { font-weight: bold; background: ${theme.colors.surfaceSecondary}; }
                @media (max-width: 767.98px) { .rich-content table { display: block; overflow-x: auto; max-width: 100%; } }
                .rich-content p { margin: 0 0 ${theme.spacing.sm}; }
            `}</style>
            {segments.map((seg, i) => {
                const m = VIDEO_UID_REGEX.exec(seg);
                if (m && seg.startsWith('<div data-cf-video')) {
                    return <VideoEmbed key={`v${i}-${m[1]}`} notificationId={notificationId} videoUid={m[1]} onOpen={src => setLightbox({ kind: 'video', src })} />;
                }
                return seg ? <div key={i} dangerouslySetInnerHTML={{ __html: seg }} /> : null;
            })}
            {lightbox && <MediaLightbox media={lightbox} onClose={closeLightbox} />}
        </div>
    );
};

export default RichContent;
